/**
 * Plain English for everything on screen, so a viewer can follow a negotiation without narration.
 * Session ids never reach the page: every principal is shown by name.
 */
import type { AgentRun, Message, Overlap, PlatformEvent, PlatformEventOf, Proposal, Resolution } from "@botallion/contracts";
import type { DashboardState } from "./state.ts";

/**
 * The name a principal is shown by: the agent's name, `a person` for any `human:<id>` (account ids never reach the
 * page), `you` when the principal is `self`, `Coordinator` for `system:coordinator`.
 */
export function nameOf(state: DashboardState, principal: string | null | undefined, self?: string | null): string {
  if (!principal) return "nobody";
  if (principal === "broadcast") return "everyone";
  if (self && principal === self) return "you";
  const session = state.sessions[principal];
  if (session) return session.agentName;
  const colon = principal.indexOf(":");
  if (colon > 0) {
    if (principal.slice(0, colon) === "human") return "a person";
    const rest = principal.slice(colon + 1);
    return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : principal;
  }
  return principal;
}

/** `bravo (WP-22)`. */
export function agentLabel(state: DashboardState, sessionId: string): string {
  const s = state.sessions[sessionId];
  return s ? `${s.agentName} (${s.wpId})` : nameOf(state, sessionId);
}

/** Replace any session id in free text with its agent name. */
export function withNames(state: DashboardState, text: string): string {
  return text.replace(/\b(ses_[A-Za-z0-9_]+)\b/g, (id) => state.sessions[id]?.agentName ?? id);
}

const KIND_LABEL: Record<Overlap["kind"], string> = {
  path: "Path",
  symbol: "Symbol",
  contract: "Contract",
  migration: "Migration",
  diff: "Pushed changes",
};

export function overlapKindLabel(kind: Overlap["kind"]): string {
  return KIND_LABEL[kind];
}

const STATUS_LABEL: Record<Overlap["status"], string> = {
  open: "Open",
  negotiating: "Negotiating",
  resolved: "Settled",
  escalated: "Escalated to a person",
  released: "Released",
};

export function overlapStatusLabel(status: Overlap["status"]): string {
  return STATUS_LABEL[status];
}

/** What a set of terms means, in one sentence, with names. */
export function describeTerms(state: DashboardState, p: Proposal | Resolution): string {
  const n = (id: string) => nameOf(state, id);
  switch (p.outcome) {
    case "sequence": {
      const [first, second] = p.terms.order;
      return `${n(first ?? "")} lands first, then ${n(second ?? "")}.`;
    }
    case "split": {
      const parts = p.terms.scopes.map(({ sessionId, scope }) => {
        const items = [...scope.paths, ...scope.symbols, ...scope.contracts, ...(scope.migration ? ["the migration lane"] : [])];
        return `${n(sessionId)} narrows to ${items.length ? items.join(", ") : "nothing"}`;
      });
      return `Split: ${parts.join("; ")}.`;
    }
    case "handoff":
      return `${n(p.terms.from)} hands this to ${n(p.terms.to)}.`;
    case "shared":
      return "Both keep working on it, sharing the file, and are told of each other's pushes.";
    case "escalated":
      return `Handed to a person to decide. ${p.terms.positions.map((x) => `${n(x.sessionId)}: "${x.position}"`).join(" ")}`;
  }
}

const OUTCOME_LABEL: Record<Resolution["outcome"], string> = {
  sequence: "sequence",
  split: "split",
  handoff: "handoff",
  shared: "shared",
  escalated: "escalation",
};

export function outcomeLabel(outcome: Resolution["outcome"]): string {
  return OUTCOME_LABEL[outcome];
}

/** What a message does in the protocol, as a verb phrase after the sender's name. */
export function messageVerb(m: Message): string {
  switch (m.type) {
    case "propose_split":
      return "proposes a split";
    case "propose_sequence":
      return "proposes an order";
    case "propose_handoff":
      return "proposes a handoff";
    case "counter":
      return `counters with ${m.proposal ? `a ${outcomeLabel(m.proposal.outcome)}` : "a new offer"}`;
    case "accept":
      return "accepts";
    case "reject":
      return "rejects";
    case "yield":
      return "yields and goes second";
    case "info":
      return "notes";
  }
}

/** How a resolution came about. */
export function viaLabel(state: DashboardState, r: Resolution): string {
  const who = r.agreedBy.map((p) => nameOf(state, p)).join(" and ");
  switch (r.via) {
    case "agreement":
      return `agreed by ${who}`;
    case "yield":
      return `${who} yielded`;
    case "timeout":
      return "no move in time, so the protocol applied its default";
    case "rule":
      return `by rule (${who})`;
    case "human":
      return `decided by ${who}`;
  }
}

/**
 * Whose move it is on an overlap, from the protocol in AGENTS.md: the side that raised it (side B) opens; after
 * that, whoever did not post last answers. Null when nobody needs to move.
 */
export function nextMove(state: DashboardState, o: Overlap, canApprove = false): string | null {
  if (o.status === "resolved" || o.status === "released") return null;
  if (o.status === "escalated") return canApprove ? "This needs a decision from you." : "This needs a decision from a person.";
  if (o.kind === "migration") return "The migration lane settles this by rule.";
  const thread = state.messages.filter((m) => m.overlapId === o.id && m.type !== "info");
  const last = thread.at(-1);
  if (!last) return `${nameOf(state, o.sessionB)} raised it, so ${nameOf(state, o.sessionB)} makes the first offer.`;
  const other = last.from === o.sessionA ? o.sessionB : o.sessionA;
  if (last.type === "reject") return `${nameOf(state, last.from)} rejected. ${nameOf(state, other)} to make a new offer.`;
  return `Waiting for ${nameOf(state, other)} to answer.`;
}

/** Counters and rejects posted on an overlap. Three escalate it. */
export function failedRounds(state: DashboardState, overlapId: string): number {
  return state.messages.filter((m) => m.overlapId === overlapId && (m.type === "counter" || m.type === "reject")).length;
}

/** Where an event belongs on screen, for follow mode. */
export type ViewId = "overview" | "negotiations" | "plan" | "verdicts" | "deploys";

/** One line for the activity feed. */
export interface FeedLine {
  seq: number;
  ts: string;
  text: string;
  tone: "info" | "warn" | "block" | "good" | "human";
  view: ViewId;
}

const short = (sha: string) => sha.slice(0, 7);

/** A feed line for an event, or null for events too small to mention (heartbeats). */
export function describeEvent(state: DashboardState, e: PlatformEvent): FeedLine | null {
  const line = (text: string, tone: FeedLine["tone"], view: ViewId): FeedLine => ({ seq: e.seq, ts: e.ts, text, tone, view });
  const n = (id: string | null | undefined) => nameOf(state, id);
  switch (e.type) {
    case "session.heartbeat":
      return null;
    case "session.registered": {
      const s = e.data.session;
      const title = state.packages[s.wpId]?.title;
      return line(`${s.agentName}${s.vendor ? ` (${s.vendor})` : ""} joined to deliver ${s.wpId}${title ? ` ${title}` : ""}.`, "info", "overview");
    }
    case "session.updated": {
      const s = e.data.session;
      const what = { active: "is active again", idle: "went quiet (no heartbeat for 2 minutes)", landed: "landed its work", expired: "expired; its claims are released" }[s.status];
      return line(`${s.agentName} ${what}.`, s.status === "landed" ? "good" : s.status === "active" ? "info" : "warn", "overview");
    }
    case "claim.declared": {
      const c = e.data.claim;
      const items = [...c.scope.paths, ...c.scope.symbols, ...c.scope.contracts, ...(c.scope.migration ? ["the migration lane"] : [])];
      const raised = e.data.overlapIds.length;
      const tail = c.status === "queued" ? " Queued for the migration lane." : raised ? ` Raised ${raised} overlap${raised === 1 ? "" : "s"}.` : "";
      return line(`${n(c.sessionId)} claimed ${items.join(", ")} (${c.mode}).${tail}`, raised ? "warn" : "info", "overview");
    }
    case "claim.updated": {
      const c = e.data.claim;
      return line(`${n(c.sessionId)}'s claim is now ${c.status}${c.mode === "shared" ? ", shared" : ""}.`, "info", "overview");
    }
    case "overlap.detected": {
      const o = e.data.overlap;
      return line(`${o.severity === "block" ? "Blocking" : "Warning"}: ${withNames(state, o.summary)}.`, o.severity === "block" ? "block" : "warn", "negotiations");
    }
    case "overlap.updated": {
      const o = e.data.overlap;
      return line(`${overlapKindLabel(o.kind)} overlap between ${n(o.sessionA)} and ${n(o.sessionB)}: ${overlapStatusLabel(o.status).toLowerCase()}.`, o.status === "resolved" ? "good" : o.status === "escalated" ? "human" : "info", "negotiations");
    }
    case "message.posted": {
      const m = e.data.message;
      if (m.type === "info") return line(`${n(m.from)} to ${n(m.to)}: ${withNames(state, m.body)}`, "info", m.overlapId ? "negotiations" : "overview");
      const terms = m.proposal ? ` ${describeTerms(state, m.proposal)}` : "";
      return line(`${n(m.from)} ${messageVerb(m)}.${terms}`, "info", "negotiations");
    }
    case "resolution.recorded": {
      const r = e.data.resolution;
      if (r.outcome === "escalated") return line(`Escalated to a person: ${withNames(state, r.terms.summary)}.`, "human", "negotiations");
      return line(`Settled (${outcomeLabel(r.outcome)}, ${viaLabel(state, r)}): ${describeTerms(state, r)}`, r.via === "human" ? "human" : "good", "negotiations");
    }
    case "migration_lane.updated": {
      const l = e.data.lane;
      const holder = l.holder ? `${n(l.holder.sessionId)} holds the migration lane` : "The migration lane is free";
      const queue = l.queue.length ? `; waiting: ${l.queue.map((q) => n(q.sessionId)).join(", ")}` : "";
      return line(`${holder}${queue}.`, "info", "overview");
    }
    case "plan.submitted":
      return line(`Planner submitted "${e.data.plan.title}": ${e.data.plan.packages.length} packages. Waiting for approval.`, "human", "plan");
    case "plan.approved":
      return line(`${n(e.data.plan.approvedBy)} approved the work plan "${e.data.plan.title}".`, "human", "plan");
    case "plan.amended": {
      const a = e.data.amendment;
      const what = [a.added.length ? `added ${a.added.join(", ")}` : "", a.superseded.length ? `superseded ${a.superseded.map((s) => s.wpId).join(", ")}` : ""].filter(Boolean).join("; ");
      return line(`${n(a.amendedBy)} amended "${e.data.plan.title}"${what ? `: ${what}` : ""}. ${a.reason}`, "info", "plan");
    }
    case "package.updated": {
      const p = e.data.package;
      return line(`${p.id} ${p.title}: ${p.status.replace("_", " ")}.`, p.status === "landed" ? "good" : p.status === "failed" ? "block" : "info", "plan");
    }
    case "diff.ingested": {
      const d = e.data.diff;
      const raised = e.data.overlapIds.length;
      return line(`${n(d.sessionId)} pushed ${short(d.commit)} touching ${d.files.map((f) => f.path).join(", ")}.${raised ? ` Raised ${raised} overlap${raised === 1 ? "" : "s"}.` : ""}`, raised ? "block" : "info", "overview");
    }
    case "verdict.recorded": {
      const v = e.data.verdict;
      const met = v.results.filter((r) => r.met).length;
      return line(`Verifier: ${v.wpId} at ${short(v.commit)} ${v.pass ? "passes" : "fails"} (${met} of ${v.results.length} criteria met).`, v.pass ? "good" : "block", "verdicts");
    }
    case "land.updated": {
      const l = e.data.land;
      const why = l.reasons.length ? `: ${withNames(state, l.reasons.join("; "))}` : "";
      return line(`${n(l.sessionId)}'s land of ${short(l.commit)} is ${l.status}${why}.`, l.status === "merged" ? "good" : l.status === "rejected" || l.status === "conflict" || l.status === "failed" ? "block" : "info", "plan");
    }
    case "deploy.updated": {
      const d = e.data.deploy;
      if (d.approvedBy) return line(`${n(d.approvedBy)} approved promoting ${d.branch} to production.`, "human", "deploys");
      return line(`${d.kind === "production" ? "Production" : `Preview of ${d.branch}`} deploy ${d.status.replace("_", " ")}.`, d.status === "succeeded" ? "good" : d.status === "failed" ? "block" : "info", "deploys");
    }
    case "run.updated": {
      const r = e.data.run;
      // Only a new status or reason makes a line: a run also reports when its agent joins and as its budget fills.
      const before = previousRun(state, e);
      if (before && before.status === r.status && before.reason === r.reason) return null;
      const who = r.agentName ? `${r.agentName} (${r.wpId})` : r.wpId;
      return line(`${who} is ${runStatusLabel(r.status)} on the ${runnerLabel(r.runner)}: ${r.reason}.`, RUN_TONE[r.status], "overview");
    }
  }
}

/** How a run's status reads. */
export function runStatusLabel(status: AgentRun["status"]): string {
  return status.replaceAll("_", " ");
}

/** Where a run's agent runs, in words. */
export function runnerLabel(runner: AgentRun["runner"]): string {
  return runner === "container" ? "Container" : "spare machine";
}

export const RUN_TONE: Record<AgentRun["status"], FeedLine["tone"]> = { starting: "info", working: "good", waiting_on_negotiation: "warn", done: "good", failed: "block" };

/** The same run as reported before this event, from the log (the latest report is usually a few events back). */
function previousRun(state: DashboardState, e: PlatformEventOf<"run.updated">): AgentRun | null {
  for (let i = state.log.length - 1; i >= 0; i--) {
    const x = state.log[i]!;
    if (x.seq < e.seq && x.type === "run.updated" && x.data.run.id === e.data.run.id) return x.data.run;
  }
  return null;
}
